import { toast } from 'react-toastify';

type FetchResult<T> = {
  response: Response;
  data: T;
};

const isAuthEndpoint = (url: string): boolean =>
  url.includes('/api/signup') || url.includes('/api/login');

async function wrappedFetch<T>(
  input: RequestInfo,
  init?: RequestInit
): Promise<FetchResult<T>> {
  const response = await fetch(input, init);

  if (!response.ok) {
    console.error(response);

    // Try to parse error details from response body
    let errorMessage = response.statusText;
    let errorDetails = null;

    try {
      // First, try to clone the response so we can read it multiple times
      const clonedResponse = response.clone();

      // Try to parse as JSON first
      try {
        const errorData = await clonedResponse.json();

        if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        }
        errorDetails = errorData;
      } catch (_jsonError) {
        // If JSON parsing fails, try reading as text
        try {
          const textResponse = await response.text();

          if (textResponse && textResponse.trim()) {
            errorMessage = textResponse;
            errorDetails = { text: textResponse };
          }
        } catch (_textError) {
          console.error('Could not read response body:', _textError);
        }
      }
    } catch (e) {
      console.error('Error reading response:', e);
      // If all parsing fails, stick with statusText
    }

    // Create enhanced error object with response details
    const error = new FetchError(response, errorMessage, errorDetails);

    // Don't show toast for errors that will be handled by form (signup, login, etc.)
    // These endpoints should handle their own error display
    const url = typeof input === 'string' ? input : input.url;
    if (!isAuthEndpoint(url)) {
      toast.error('Error: ' + errorMessage);
    }

    throw error;
  }

  const data = await response.json();

  if (init && init.method !== 'GET') {
    // Don't show success toast for auth endpoints either
    const url = typeof input === 'string' ? input : input.url;
    if (!isAuthEndpoint(url)) {
      toast.success('Success');
    }
  }

  return { response, data };
}

type PostData = {
  [key: string]: boolean | number | string | null | PostData;
};

function cookie(name: string) {
  const match = document.cookie.match(
    new RegExp('(^|;\\s*)(' + name + ')=([^;]*)')
  );
  return match ? decodeURIComponent(match[3]) : '';
}

function withData(method: 'POST' | 'PUT') {
  return function json<T>(url: string, data: PostData) {
    return wrappedFetch<T>(url, {
      method,
      mode: 'cors',
      cache: 'no-cache',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json;charset=UTF-8',
        'X-Requested-With': 'XMLHttpRequest',
        'X-XSRF-TOKEN': cookie('XSRF-TOKEN'),
      },
      body: JSON.stringify(data),
    });
  };
}

const post = withData('POST');
const put = withData('PUT');

async function fetchJson(url: string) {
  const { data } = await wrappedFetch(url);
  return data;
}

export class FetchError extends Error {
  response: Response;
  details?: unknown;
  statusCode: number;

  constructor(response: Response, message?: string, details?: unknown) {
    super(message ?? response.statusText);
    this.name = 'FetchError';
    this.response = response;
    this.details = details;
    this.statusCode = response.status;
  }
}

/**
 * Minimal fetcher, favor using this everywhere if possible
 * @param url
 */
export async function fetcher<T>(url: string): Promise<T> {
  const res = await fetch(url);

  if (!res.ok) {
    throw new FetchError(res);
  }

  return res.json();
}

export { wrappedFetch as fetch, fetchJson, post, put };
